import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthFetch } from '@/hooks/useAuthFetch';
import { AIAnalytics } from '@/components/charts/AIAnalytics';
import { InviteeTracker } from '@/components/share/InviteeTracker';
import { InviteModal } from '@/components/share/InviteModal';
import { HiOutlineShare, HiOutlineCheckCircle, HiOutlineUsers, HiOutlineClock, HiOutlineChartBar, HiOutlineEnvelope } from 'react-icons/hi2';
import type { Interview, InterviewResponse } from '@acde/shared';

const API_URL = import.meta.env.VITE_API_URL || '/api';

interface ResponseSummary {
  id: string;
  participantId: string;
  participantName?: string;
  participantEmail?: string;
  status: string;
  startedAt: string;
  submittedAt?: string;
  duration?: number;
  transcriptLength: number;
  attachmentCount: number;
}

type ViewTab = 'responses' | 'analytics';

export function ResultsPage() {
  const { id: interviewId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authFetch } = useAuthFetch();

  // Data state
  const [interview, setInterview] = useState<Interview | null>(null);
  const [responses, setResponses] = useState<ResponseSummary[]>([]);
  const [selectedResponse, setSelectedResponse] = useState<InterviewResponse | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ViewTab>('analytics');
  const [copiedLink, setCopiedLink] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Load interview results
  useEffect(() => {
    if (!interviewId) return;

    const loadResults = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const res = await authFetch(`${API_URL}/interviews/${interviewId}/results`);
        if (res.ok) {
          const data = await res.json();
          setInterview(data.interview);
          setResponses(data.responses || []);
        } else {
          const errorData = await res.json();
          setError(errorData.error || 'Failed to load results');
        }
      } catch {
        setError('Failed to load interview results');
      } finally {
        setIsLoading(false);
      }
    };

    loadResults();
  }, [interviewId, authFetch]);

  // Load full response details
  const loadResponseDetails = async (responseId: string) => {
    try {
      const res = await authFetch(`${API_URL}/responses/${responseId}?interviewId=${interviewId}`);
      if (res.ok) {
        const data = await res.json();
        setSelectedResponse(data.response);
      }
    } catch (err) {
      console.error('[Results] Failed to load response details:', err);
    }
  };

  // Copy share link
  const copyShareLink = async () => {
    if (!interview) return;
    const shareUrl = `${window.location.origin}/respond/${interview.shareToken}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted">Loading results...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center py-12">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Unable to Load Results</h3>
          <p className="text-muted mb-4">{error}</p>
          <button onClick={() => navigate('/')} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  if (!interview) return null;

  const completedResponses = responses.filter((r) => r.status === 'submitted').length;
  const avgDuration = responses.length > 0
    ? Math.round(responses.reduce((sum, r) => sum + (r.duration || 0), 0) / responses.length)
    : 0;

  return (
    <div className="max-w-6xl mx-auto py-4 px-4 sm:px-0 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => navigate('/')}
            className="text-muted hover:text-foreground text-sm mb-2 flex items-center gap-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground truncate">{interview.title}</h1>
          {interview.description && (
            <p className="text-muted mt-1 text-sm sm:text-base line-clamp-2">{interview.description}</p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link to={`/interviews/${interview.id}/edit`} className="btn">
            Edit
          </Link>
          {interview.status === 'active' && (
            <>
              <button onClick={() => setShowInviteModal(true)} className="btn flex items-center gap-2">
                <HiOutlineEnvelope className="w-4 h-4" />
                <span className="hidden sm:inline">Invite</span>
              </button>
              <button onClick={copyShareLink} className="btn btn-primary flex items-center gap-2">
                {copiedLink ? (
                  <>
                    <HiOutlineCheckCircle className="w-4 h-4" />
                    <span className="hidden sm:inline">Copied!</span>
                  </>
                ) : (
                  <>
                    <HiOutlineShare className="w-4 h-4" />
                    <span className="hidden sm:inline">Share Link</span>
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-6">
        <div className="card bg-gradient-to-br from-primary/5 to-transparent">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <HiOutlineUsers className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-foreground">{responses.length}</div>
              <div className="text-xs text-muted truncate">Responses</div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-500/5 to-transparent">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-green-500/10 flex items-center justify-center shrink-0">
              <HiOutlineCheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-foreground">{completedResponses}</div>
              <div className="text-xs text-muted truncate">Completed</div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-purple-500/5 to-transparent">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-purple-500/10 flex items-center justify-center shrink-0">
              <HiOutlineClock className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-foreground">{formatDuration(avgDuration)}</div>
              <div className="text-xs text-muted truncate">Avg Time</div>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-orange-500/5 to-transparent">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-orange-500/10 flex items-center justify-center shrink-0">
              <HiOutlineChartBar className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600" />
            </div>
            <div className="min-w-0">
              <div className="text-xl sm:text-2xl font-bold text-foreground">{interview.questions.length}</div>
              <div className="text-xs text-muted truncate">Questions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Invitee Tracking */}
      {interview.status === 'active' && (
        <InviteeTracker
          interviewId={interview.id}
          interviewTitle={interview.title}
          shareToken={interview.shareToken}
        />
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto">
          {[
            { id: 'analytics' as const, label: 'Analytics', icon: HiOutlineChartBar },
            { id: 'responses' as const, label: 'Responses', count: responses.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-foreground'
              }`}
            >
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
              {tab.count !== undefined && (
                <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-user-msg">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content -- both tabs stay mounted to preserve state */}
      <div className={activeTab === 'analytics' ? '' : 'hidden'}>
        <AIAnalytics 
          interviewId={interviewId!} 
          responseCount={completedResponses}
          autoLoad={true}
        />
      </div>

      <div className={activeTab === 'responses' ? '' : 'hidden'}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Response List */}
          <div className="space-y-3">
            {responses.length === 0 ? (
              <div className="card text-center py-12">
                <HiOutlineUsers className="w-12 h-12 mx-auto text-muted mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No responses yet</h3>
                <p className="text-muted mb-4">Share the interview link to start collecting responses.</p>
                {interview.status === 'active' && (
                  <button onClick={copyShareLink} className="btn btn-primary">
                    Copy Share Link
                  </button>
                )}
              </div>
            ) : (
              responses.map((response) => (
                <button
                  key={response.id}
                  onClick={() => loadResponseDetails(response.id)}
                  className={`w-full text-left card hover:border-primary transition-all ${
                    selectedResponse?.id === response.id ? 'border-primary' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-foreground">
                        {response.participantName || response.participantEmail || `Participant ${response.participantId.slice(0, 8)}`}
                      </div>
                      <div className="text-xs text-muted flex items-center gap-2 mt-1">
                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                          response.status === 'submitted'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}>
                          {response.status}
                        </span>
                        <span>{formatDate(response.submittedAt || response.startedAt)}</span>
                        <span>•</span>
                        <span>{formatDuration(response.duration)}</span>
                      </div>
                    </div>
                    <div className="text-sm text-muted">
                      {response.transcriptLength} messages
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Selected Response Detail */}
          <div className="card min-h-[400px]">
            {selectedResponse ? (
              <div>
                <h3 className="font-semibold text-foreground mb-4">
                  Transcript - {selectedResponse.participantName || 'Participant'}
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedResponse.transcript.map((entry) => (
                    <div key={entry.id} className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                        entry.role === 'user'
                          ? 'bg-primary text-white'
                          : 'bg-user-msg text-foreground'
                      }`}>
                        {entry.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center">
                <div>
                  <svg className="w-12 h-12 mx-auto text-muted mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className="text-muted">Select a response to view the transcript</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {interview.status === 'active' && (
        <InviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          interviewId={interview.id}
          interviewTitle={interview.title}
          shareToken={interview.shareToken}
        />
      )}
    </div>
  );
}
